import { doc, serverTimestamp, setDoc, writeBatch } from 'firebase/firestore';

import { useAuth } from '../contextProviders/AuthProvider';
import { useData } from '../contextProviders/DataProvider';
import { firestore } from '../firebase';
import {
  USGA_COURSE_DRAFT_COLLECTION,
  USGA_COURSE_SEARCH_COLLECTION,
  USGA_COURSE_SEARCH_STATUSES,
} from '../types/UsgaCourseSearch';

import type { UsgaCourseDraft, UsgaCourseSearch } from '../types/UsgaCourseSearch';

/** God only: the requests behind "Import from the USGA" (handled by the server). */
export default function useUsgaCourseSearchMutators() {
  const { isGod } = useAuth();
  const { userState } = useData();
  const profileId = userState.status === 'ready' ? userState.userProfile?.itemId ?? null : null;

  /** Asks the server to search the USGA for courses by name (handleUsgaCourseSearchRequested). */
  const requestUsgaCourseSearch = async (name: string): Promise<void> => {
    if (!isGod || !profileId) {
      console.error('Permission denied: only god users can search USGA courses');
      return;
    }
    const search: UsgaCourseSearch = {
      itemId: profileId,
      status: USGA_COURSE_SEARCH_STATUSES.REQUESTED,
      requestedBy: profileId,
      requestedAt: serverTimestamp(),
      finishedAt: null,
      name: name.trim(),
      results: null,
      message: null,
    };
    await setDoc(doc(firestore, USGA_COURSE_SEARCH_COLLECTION, profileId), search);
  };

  /**
   * Asks the server to read each USGA course's details and tee sets into a draft
   * (handleUsgaCourseDraftRequested) — one request per course, so several import at once.
   */
  const requestUsgaCourseDrafts = async (usgaCourseIds: string[]): Promise<void> => {
    if (!isGod || !profileId) {
      console.error('Permission denied: only god users can import USGA courses');
      return;
    }
    const batch = writeBatch(firestore);
    for (const usgaCourseId of usgaCourseIds) {
      const draft: UsgaCourseDraft = {
        itemId: usgaCourseId,
        status: USGA_COURSE_SEARCH_STATUSES.REQUESTED,
        requestedBy: profileId,
        requestedAt: serverTimestamp(),
        finishedAt: null,
        course: null,
        tees: null,
        message: null,
      };
      batch.set(doc(firestore, USGA_COURSE_DRAFT_COLLECTION, usgaCourseId), draft);
    }
    await batch.commit();
  };

  return { requestUsgaCourseSearch, requestUsgaCourseDrafts };
}
